import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import AdminLayout from '../components/AdminLayout';
import { bookingServiceAPI, Booking } from '../services/bookingServiceAPI';
import { ticketService } from '../services/ticketService';
import { authService } from '../services/authService';
import RiyalSymbol from '../components/RiyalSymbol';
import QRCode from 'react-qr-code';
import { configService } from '../services/ConfigService';
import '../styles/ConfirmedBookings.css';

function BookingsManagement() {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<Booking[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterDate, setFilterDate] = useState('');
  const [showScanDetails, setShowScanDetails] = useState<{booking: Booking, show: boolean} | null>(null);
  const [showPaymentQR, setShowPaymentQR] = useState<{booking: Booking, show: boolean} | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tickets, setTickets] = useState<any[]>([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState<string[]>([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [ticketsUsageCache, setTicketsUsageCache] = useState<{[key: string]: number}>({});
  const [showGenerateTicketsModal, setShowGenerateTicketsModal] = useState<{booking: Booking, show: boolean} | null>(null);
  const [ticketCount, setTicketCount] = useState(1);
  const [generatingTickets, setGeneratingTickets] = useState(false);
  const [generateError, setGenerateError] = useState('');

  const [showManualBookingModal, setShowManualBookingModal] = useState(false);
  const [newBookingData, setNewBookingData] = useState({
    customerName: '', phone: '', email: '', bookingType: 'hourly',
    boothType: 'classic', boothDisplayName: 'كابينة Classic', eventDate: '', eventTime: '', location: '',
    hours: 1, totalTickets: 0, freeTickets: 0, additionalTickets: 0,
    totalAmount: 0, paidAmount: 0, paymentStatus: 'completed',
    city: '', cityName: '', cityFee: 0,
    discount: 0
  });
  const [creatingManualBooking, setCreatingManualBooking] = useState(false);
  const [cities, setCities] = useState<any[]>([]);
  const [customHoursMode, setCustomHoursMode] = useState(false);
  const [isCalculated, setIsCalculated] = useState(false); // To prevent loop

  const [editingBooking, setEditingBooking] = useState<Booking | null>(null);
  const [editFormData, setEditFormData] = useState<Partial<Booking>>({});
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  const getCalculatedBasePrice = () => {
    const config = configService.getConfig();
    let basePrice = 0;

    const selectedBooth = config.booths?.find((b: any) => b.slug === newBookingData.boothType);
    const isCustomPricing = selectedBooth?.customPricing?.enabled;

    if (newBookingData.bookingType === 'perPhoto') {
      basePrice = isCustomPricing && selectedBooth?.customPricing?.ticketSystem
          ? (selectedBooth.customPricing.ticketSystem.installationFee || config.ticketSystem.installationFee)
          : config.ticketSystem.installationFee;
    } else {
      const packageList = isCustomPricing && selectedBooth?.customPricing?.hourlyPackages && selectedBooth.customPricing.hourlyPackages.length > 0
          ? selectedBooth.customPricing.hourlyPackages
          : config.hourlyPackages;
      const selectedPackage = packageList?.find((p: any) => p.hours === newBookingData.hours);
      basePrice = selectedPackage ? selectedPackage.price : 0;
    }
    
    return basePrice;
  };

  // Price Calculation logic
  useEffect(() => {
    if (!showManualBookingModal) return;

    try {
      const calculatedBasePrice = getCalculatedBasePrice();

      // Calculate discount amount
      const discountPercentage = newBookingData.discount || 0;
      const discountVal = (calculatedBasePrice * discountPercentage) / 100;

      // Add city fee
      const total = calculatedBasePrice + (newBookingData.cityFee || 0) - discountVal;

      if (!isCalculated) {
        setNewBookingData(prev => ({
          ...prev,
          totalAmount: total,
          paidAmount: total
        }));
      }
    } catch (error) {
      console.warn("Could not calculate dynamic price:", error);
    }
  }, [newBookingData.bookingType, newBookingData.hours, newBookingData.cityFee, newBookingData.discount, newBookingData.boothType, showManualBookingModal]);

  const [viewMode, setViewMode] = useState<'cards'|'list'>(
    (localStorage.getItem('fotomista_bookings_viewMode') as 'cards'|'list') || 'cards'
  );

  const handleViewModeChange = (mode: 'cards'|'list') => {
    setViewMode(mode);
    localStorage.setItem('fotomista_bookings_viewMode', mode);
  };

  useEffect(() => {
    loadBookings();
    fetchCities();
  }, []);

  const fetchCities = async () => {
    try {
      const token = localStorage.getItem('fotomista_token');
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5002'}/api/cities`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setCities(data.cities.filter((c: any) => c.isActive));
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const handleCreateManualBooking = async (e: React.FormEvent) => {
    e.preventDefault();
    setCreatingManualBooking(true);
    try {
      const token = localStorage.getItem('fotomista_token') || 'temp-admin-token-123';
      const baseUrl = (process.env.REACT_APP_API_URL || 'http://localhost:5002').replace(/\/$/, '');
      
      const calculatedBasePrice = getCalculatedBasePrice();
      const discountAmountVal = (calculatedBasePrice * (newBookingData.discount || 0)) / 100;

      const payload = {
        ...newBookingData,
        baseAmount: newBookingData.totalAmount + discountAmountVal, // restore pure base + cityFee before discount
        discountAmount: discountAmountVal,
        discountCode: newBookingData.discount > 0 ? 'خصم إداري خاص' : undefined,
        hours: newBookingData.bookingType === 'hourly' ? newBookingData.hours : undefined,
        totalTickets: newBookingData.bookingType === 'perPhoto' ? newBookingData.totalTickets : undefined,
        sendEmail: true
      };

      const response = await fetch(`${baseUrl}/api/bookings/admin/manual`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      const result = await response.json();
      if (result.success) {
        alert('تم إضافة الحجز بنجاح!');
        setShowManualBookingModal(false);
        loadBookings();
      } else {
        alert('حدث خطأ: ' + (result.message || 'فشل في الإضافة'));
      }
    } catch (err) {
      console.error(err);
      alert('خطأ في الاتصال بالخادم');
    } finally {
      setCreatingManualBooking(false);
    }
  };

  useEffect(() => {
    if (bookings.length > 0) {
      loadTicketsUsageFromAPI();
    }
  }, [bookings]);

  const loadTicketsUsageFromAPI = async () => {
    try {
      const cacheData: {[key: string]: number} = {};
      
      for (const booking of bookings) {
        const bookingId = booking.id || booking._id || '';
        if (bookingId && booking.bookingType === 'perPhoto') {
          try {
            const { bookingService } = await import('../services/BookingService');
            const tickets = await bookingService.getTicketsByBooking(bookingId);
            const usedCount = tickets.filter((t: any) => t.used).length;
            cacheData[bookingId] = usedCount;
          } catch (error) {
            console.warn(`Failed to load tickets for booking ${bookingId}:`, error);
            cacheData[bookingId] = 0;
          }
        }
      }
      
      setTicketsUsageCache(cacheData);
      console.log('📊 تم تحميل بيانات استخدام التذاكر من قاعدة البيانات:', cacheData);
    } catch (error) {
      console.error('❌ خطأ في تحميل بيانات الاستخدام من API:', error);
    }
  };

  const loadBookings = async () => {
    try {
      setLoading(true);
      const confirmedBookings = await bookingServiceAPI.getConfirmedBookings();
      setBookings(confirmedBookings);
      setFilteredBookings(confirmedBookings);
    } catch (err: any) {
      setError(err.message || 'فشل جلب الحجوزات');
      console.error('Error loading bookings:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let filtered = bookings;

    if (searchTerm) {
      filtered = filtered.filter(booking => 
        booking.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.phone.includes(searchTerm) ||
        booking.bookingCode.toLowerCase().includes(searchTerm.toLowerCase()) ||
        booking.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterDate) {
      filtered = filtered.filter(booking => 
        booking.eventDate === filterDate
      );
    }

    setFilteredBookings(filtered);
  }, [searchTerm, filterDate, bookings]);

  const handleLogout = () => {
    authService.logout();
    navigate('/admin-login');
  };

  const exportToCSV = () => {
    const sortedBookings = [...filteredBookings].sort((a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime());
    
    const headers = ['اسم العميل', 'رقم الحجز', 'تاريخ المناسبة', 'رقم الهاتف', 'نوع البوث', 'نوع النظام', 'المدة / التذاكر', 'الموقع', 'المدفوع', 'المتبقي', 'الإجمالي'];
    
    const csvContent = [
      headers.join(','),
      ...sortedBookings.map(b => {
        const date = new Date(b.eventDate).toLocaleDateString('en-GB');
        const systemType = b.bookingType === 'hourly' ? 'نظام الساعات' : 'نظام التذاكر';
        const duration = b.bookingType === 'hourly' ? `${b.hours} ساعة` : `${b.totalTickets} تذكرة`;
        const total = b.totalAmount || 0;
        const paid = b.paidAmount ?? total;
        const remaining = Math.max(0, total - paid);
        
        const row = [
          `"${b.customerName}"`,
          `"${b.bookingCode}"`,
          `"${date}"`,
          `"${b.phone}"`,
          `"${b.boothDisplayName || b.boothType}"`,
          `"${systemType}"`,
          `"${duration}"`,
          `"${b.location}"`,
          `"${paid}"`,
          `"${remaining}"`,
          `"${total}"`
        ];
        return row.join(',');
      })
    ].join('\n');

    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `تقرير_الحجوزات_${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleSaveEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingBooking) return;
    
    setIsSavingEdit(true);
    try {
      await bookingServiceAPI.updateBooking(editingBooking.id || editingBooking._id as string, editFormData);
      alert('تم تحديث بيانات الحجز بنجاح!');
      setEditingBooking(null);
      loadBookings();
    } catch (err: any) {
      alert('خطأ أثناء التحديث: ' + err.message);
    } finally {
      setIsSavingEdit(false);
    }
  };

  const getBookingTypeLabel = (type: string) => {
    return type === 'hourly' ? 'نظام الساعات' : 'نظام التذاكر';
  };

  const getUsedTicketsCount = (booking: Booking) => {
    const bookingId = booking.id || booking._id || '';
    if (!bookingId) return 0;
    
    if (ticketsUsageCache[bookingId] !== undefined) {
      return ticketsUsageCache[bookingId];
    }
    
    return 0;
  };

  const getRemainingTicketsCount = (booking: Booking) => {
    const totalTickets = (booking.freeTickets || 0) + (booking.additionalTickets || 0) + (booking.additionalTicketsCount || 0);
    const usedTickets = getUsedTicketsCount(booking);
    return Math.max(0, totalTickets - usedTickets);
  };

  const loadTickets = async (bookingId: string) => {
    try {
      setLoadingTickets(true);
      
      const response = await ticketService.getTicketsByBooking(bookingId);
      const originalTickets = response.tickets || [];
      
      const { bookingService } = await import('../services/BookingService');
      const booking = await bookingService.getBookingById(bookingId);
      const additionalTickets = booking?.additionalTicketsPurchased || [];
      
      const allTickets = [
        ...originalTickets.map((ticket: any) => ({
          ...ticket,
          type: 'original'
        })),
        ...additionalTickets.map((ticket: any) => ({
          id: `additional_${ticket.ticketNumber}`,
          ticketNumber: ticket.ticketNumber,
          qrCode: ticket.qrCode,
          used: ticket.used,
          usedAt: ticket.usedAt,
          type: 'additional',
          purchaseDate: ticket.purchaseDate
        }))
      ];
      
      setTickets(allTickets);
      
      const usedCount = allTickets.filter((t: any) => t.used).length;
      setTicketsUsageCache(prev => ({...prev, [bookingId]: usedCount}));
      
    } catch (err) {
      console.error('Error loading tickets:', err);
      setTickets([]);
    } finally {
      setLoadingTickets(false);
    }
  };

  const handleShowScanDetails = async (booking: Booking) => {
    setShowScanDetails({ booking, show: true });
    if (booking.id || booking._id) {
      await loadTickets(booking.id || booking._id || '');
    }
  };

  const toggleDeleteMode = () => {
    setDeleteMode(!deleteMode);
    setSelectedForDelete([]);
  };

  const toggleSelectForDelete = (bookingId: string) => {
    if (selectedForDelete.includes(bookingId)) {
      setSelectedForDelete(selectedForDelete.filter(id => id !== bookingId));
    } else {
      setSelectedForDelete([...selectedForDelete, bookingId]);
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedForDelete.length === 0) return;
    setShowDeleteConfirm(true);
  };

  const handleGenerateTickets = async () => {
    if (!showGenerateTicketsModal) return;

    const booking = showGenerateTicketsModal.booking;
    const bookingId = booking.id || booking._id || '';

    setGeneratingTickets(true);
    setGenerateError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'https://fotomista-backend-production.up.railway.app'}/api/additional-tickets/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          bookingId: bookingId,
          ticketCount: ticketCount,
          customerEmail: booking.email
        })
      });

      const result = await response.json();

      if (result.success) {
        alert(`تم توليد ${ticketCount} تذاكر بنجاح وإرسالها إلى ${booking.email}`);
        setShowGenerateTicketsModal(null);

        // تحديث حالة الحجوزات
        await loadBookings();
      } else {
        setGenerateError(result.message || 'فشل توليد التذاكر');
      }
    } catch (error: any) {
      console.error('Error generating tickets:', error);
      setGenerateError('حدث خطأ في توليد التذاكر');
    } finally {
      setGeneratingTickets(false);
    }
  };

  const confirmDelete = async () => {
    try {
      for (const bookingId of selectedForDelete) {
        await bookingServiceAPI.deleteBooking(bookingId);
      }
      
      await loadBookings();
      
      setSelectedForDelete([]);
      setDeleteMode(false);
      setShowDeleteConfirm(false);
      
      alert(`تم حذف ${selectedForDelete.length} حجوزات بنجاح`);
    } catch (error) {
      console.error('Error deleting bookings:', error);
      alert('حدث خطأ في حذف الحجوزات');
    }
  };

  const generateInvoice = async (booking: Booking) => {
    try {
      const bookingId = booking.id || booking._id;
      if (!bookingId) {
        alert('خطأ: معرف الحجز غير موجود');
        return;
      }

      const baseUrl = (process.env.REACT_APP_API_URL || 'https://fotomista-backend-production.up.railway.app').replace(/\/$/, '');
      
      const response = await fetch(`${baseUrl}/api/invoices/create/${bookingId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('fotomista_token') || 'temp-admin-token-123'}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        
        const pdfResponse = await fetch(`${baseUrl}/api/invoices/${data.invoice._id}/pdf`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('fotomista_token') || 'temp-admin-token-123'}`
          }
        });

        if (pdfResponse.ok) {
          window.open(`${baseUrl}/api/invoices/${data.invoice._id}/preview`, '_blank');
          alert('تم إنشاء الفاتورة بنجاح');
        } else {
          alert('تم إنشاء الفاتورة ولكن فشل في إنشاء ملف PDF');
        }
      } else {
        let errorMessage = 'خطأ غير معروف';
        try {
          const errorData = await response.json();
          errorMessage = errorData.message || 'خطأ غير معروف';
        } catch (e) {
          errorMessage = 'فشل في قراءة رسالة الخطأ';
        }
        alert('خطأ في إنشاء الفاتورة: ' + errorMessage);
      }
    } catch (error) {
      console.error('Error generating invoice:', error);
      alert('خطأ في إنشاء الفاتورة');
    }
  };

  if (loading) {
    return (
      <AdminLayout title="إدارة الحجوزات">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>جاري التحميل...</p>
        </div>
      </AdminLayout>
    );
  }

  if (error) {
    return (
      <AdminLayout title="إدارة الحجوزات">
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button onClick={loadBookings} className="retry-btn">
            إعادة المحاولة
          </button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="إدارة الحجوزات">
      <motion.div 
        className="bookings-overview"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="bookings-container">
          <div className="bookings-header">
            <div className="stats-cards">
              <div className="stat-card">
                <h3>إجمالي الحجوزات</h3>
                <p className="stat-number">{bookings.length}</p>
              </div>
              <div className="stat-card">
                <h3>حجوزات اليوم</h3>
                <p className="stat-number">
                  {bookings.filter(b => 
                    new Date(b.eventDate).toDateString() === new Date().toDateString()
                  ).length}
                </p>
              </div>
              <div className="stat-card">
                <h3>إجمالي المبيعات</h3>
                <p className="stat-number">
                  {bookings.reduce((sum, b) => sum + b.totalAmount, 0).toLocaleString('en-US')}
                  <RiyalSymbol size={20} />
                </p>
              </div>
            </div>

            <div className="management-actions" style={{ marginTop: '20px', textAlign: 'center', display: 'flex', justifyContent: 'center', gap: '10px' }}>
              <button
                onClick={() => {
                  const firstBooth = configService.getBooths()[0];
                  setNewBookingData({
                    customerName: '', phone: '', email: '', bookingType: 'hourly',
                    boothType: firstBooth?.slug || 'classic', 
                    boothDisplayName: firstBooth?.name || 'كابينة Classic', 
                    eventDate: '', eventTime: '', location: '',
                    hours: 1, totalTickets: 0, freeTickets: 0, additionalTickets: 0,
                    totalAmount: 0, paidAmount: 0, paymentStatus: 'completed',
                    city: '', cityName: '', cityFee: 0,
                    discount: 0
                  });
                  setShowManualBookingModal(true);
                }}
                style={{
                  background: '#27ae60',
                  color: 'white',
                  border: 'none',
                  padding: '10px 30px',
                  borderRadius: '25px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
              >
                + إضافة حجز يدوي
              </button>

              <button
                onClick={toggleDeleteMode}
                className={`delete-mode-btn ${deleteMode ? 'active' : ''}`}
                style={{
                  background: deleteMode ? '#e74c3c' : '#95a5a6',
                  color: 'white',
                  border: 'none',
                  padding: '10px 30px',
                  borderRadius: '25px',
                  fontSize: '16px',
                  cursor: 'pointer',
                  marginRight: '10px',
                  transition: 'all 0.3s ease'
                }}
              >
                {deleteMode ? 'إلغاء وضع الحذف' : 'تفعيل وضع الحذف'}
              </button>
              
              <button
                onClick={exportToCSV}
                style={{
                  background: '#f39c12',
                  color: 'white',
                  border: 'none',
                  padding: '10px 30px',
                  borderRadius: '25px',
                  fontSize: '16px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  marginRight: '10px',
                  transition: 'all 0.3s ease'
                }}
              >
                تحميل التقرير (CSV) 📥
              </button>
              
              {deleteMode && selectedForDelete.length > 0 && (
                <button
                  onClick={handleDeleteSelected}
                  className="delete-selected-btn"
                  style={{
                    background: '#e74c3c',
                    color: 'white',
                    border: 'none',
                    padding: '10px 30px',
                    borderRadius: '25px',
                    fontSize: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}
                >
                  حذف المحدد ({selectedForDelete.length})
                </button>
              )}
            </div>

            <div className="filters">
              <input
                type="text"
                placeholder="بحث بالاسم أو الهاتف أو رقم الحجز..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
              <input
                type="date"
                value={filterDate}
                onChange={(e) => setFilterDate(e.target.value)}
                className="date-filter"
              />
              {(searchTerm || filterDate) && (
                <button 
                  className="clear-filters"
                  onClick={() => {
                    setSearchTerm('');
                    setFilterDate('');
                  }}
                >
                  مسح التصفية
                </button>
              )}
              <div style={{ display: 'flex', background: '#ecf0f1', borderRadius: '10px', padding: '5px', gap: '5px', marginRight: 'auto' }}>
                <button 
                  onClick={() => handleViewModeChange('cards')} 
                  style={{ background: viewMode === 'cards' ? 'white' : 'transparent', border: 'none', padding: '8px 15px', borderRadius: '8px', cursor: 'pointer', fontWeight: viewMode === 'cards' ? 'bold' : 'normal', boxShadow: viewMode === 'cards' ? '0 2px 5px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.2s' }}
                >
                  كروت
                </button>
                <button 
                  onClick={() => handleViewModeChange('list')} 
                  style={{ background: viewMode === 'list' ? 'white' : 'transparent', border: 'none', padding: '8px 15px', borderRadius: '8px', cursor: 'pointer', fontWeight: viewMode === 'list' ? 'bold' : 'normal', boxShadow: viewMode === 'list' ? '0 2px 5px rgba(0,0,0,0.1)' : 'none', transition: 'all 0.2s' }}
                >
                  قائمة
                </button>
              </div>
            </div>
          </div>

          <div className="bookings-table-new">
            {filteredBookings.length === 0 ? (
              <div className="no-bookings">
                <p>لا توجد حجوزات مؤكدة</p>
              </div>
            ) : viewMode === 'list' ? (
              <div className="bookings-list" style={{ overflowX: 'auto', background: 'white', borderRadius: '15px', padding: '20px', boxShadow: '0 5px 15px rgba(0,0,0,0.05)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'right', fontSize: '14px' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ecf0f1', color: '#7f8c8d' }}>
                      <th style={{ padding: '15px' }}>رقم الحجز</th>
                      <th style={{ padding: '15px' }}>العميل</th>
                      <th style={{ padding: '15px' }}>التاريخ / الوقت</th>
                      <th style={{ padding: '15px' }}>البوث</th>
                      <th style={{ padding: '15px' }}>المالية</th>
                      <th style={{ padding: '15px' }}>الإجراءات</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredBookings.map(booking => {
                      const remain = (booking.totalAmount || 0) - ((booking.paidAmount ?? (booking.totalAmount || 0)));
                      return (
                        <tr key={booking.id} style={{ borderBottom: '1px solid #f1f2f6', color: '#333' }}>
                          <td style={{ padding: '15px', fontWeight: 'bold', color: '#2c3e50' }}>#{booking.bookingCode}</td>
                          <td style={{ padding: '15px' }}>
                            <div style={{ color: '#2c3e50', fontWeight: '500' }}>{booking.customerName || 'بدون اسم'}</div>
                            <div style={{ color: '#7f8c8d', fontSize: '12px' }}>{booking.phone}</div>
                          </td>
                          <td style={{ padding: '15px' }}>
                            <div style={{ color: '#2c3e50' }}>
                              {new Date(booking.eventDate).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              })}
                            </div>
                            <div style={{ color: '#7f8c8d', fontSize: '12px' }}>{booking.eventTime}</div>
                          </td>
                          <td style={{ padding: '15px' }}>
                            <div style={{ color: '#2c3e50' }}>{getBookingTypeLabel(booking.bookingType)}</div>
                            <div style={{ color: '#7f8c8d', fontSize: '12px' }}>
                              {booking.boothDisplayName || 'غير محدد'} 
                              {booking.bookingType === 'hourly' && ` (${booking.hours} ساعات)`}
                              {booking.bookingType === 'perPhoto' && ` (${booking.totalTickets || 0} تذكرة)`}
                            </div>
                          </td>
                          <td style={{ padding: '15px' }}>
                            <div style={{ fontWeight: 'bold', color: '#2c3e50' }}>{booking.totalAmount?.toLocaleString('en-US') || 0} <RiyalSymbol size={12} /></div>
                            {remain > 0 && <div style={{ color: '#e74c3c', fontSize: '12px' }}>متبقي: {remain.toLocaleString('en-US')}</div>}
                            {remain <= 0 && booking.totalAmount > 0 && <div style={{ color: '#27ae60', fontSize: '12px' }}>مدفوع بالكامل</div>}
                          </td>
                          <td style={{ padding: '15px' }}>
                            <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                              <button onClick={() => { setEditingBooking(booking); setEditFormData({ paidAmount: booking.paidAmount ?? booking.totalAmount, totalAmount: booking.totalAmount }); }} style={{ background: '#f39c12', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}>تعديل</button>
                              <button onClick={() => generateInvoice(booking)} style={{ background: '#ff6b35', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}>فاتورة</button>
                              <button onClick={() => window.open(`${process.env.REACT_APP_API_URL || 'https://fotomista-backend-production.up.railway.app'}/api/invoices/booking/${booking.id || booking._id}/preview`, '_blank')} style={{ background: '#2196F3', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}>معاينة</button>
                              {booking.bookingType === 'perPhoto' && (
                                <button onClick={() => {
                                  navigator.clipboard.writeText(`https://www.fotomista.com/design-your-tickets/${booking.bookingCode}`);
                                  alert('تم نسخ رابط التصميم بنجاح');
                                }} style={{ background: '#9b59b6', color: 'white', border: 'none', padding: '5px 10px', borderRadius: '5px', cursor: 'pointer', fontSize: '12px' }}>نسخ الرابط</button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="bookings-grid">
                {filteredBookings.map(booking => {
                  const usedTickets = getUsedTicketsCount(booking);
                  const remainingTickets = getRemainingTicketsCount(booking);
                  
                  const bookingId = booking.id || booking._id || '';
                  const isSelected = selectedForDelete.includes(bookingId);
                  
                  return (
                    <motion.div 
                      key={booking.id}
                      className={`booking-card ${deleteMode ? 'delete-mode' : ''} ${isSelected ? 'selected-for-delete' : ''}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      whileHover={{ y: -5 }}
                      style={{
                        border: isSelected ? '2px solid #e74c3c' : undefined,
                        backgroundColor: isSelected ? '#fee' : undefined
                      }}
                    >
                      <div className="card-header">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                          {deleteMode && (
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleSelectForDelete(bookingId)}
                              style={{
                                width: '20px',
                                height: '20px',
                                cursor: 'pointer',
                                marginTop: '5px'
                              }}
                            />
                          )}
                          <div>
                            <h3>{booking.customerName}</h3>
                            <span className="booking-code">#{booking.bookingCode}</span>
                          </div>
                        </div>
                        {booking.bookingType === 'perPhoto' && !deleteMode && (
                          <div style={{ display: 'flex', gap: '5px' }}>
                            <button
                              className="qr-payment-btn"
                              onClick={() => setShowPaymentQR({ booking, show: true })}
                              title="رابط شراء تذاكر إضافية"
                            >
                              <span className="qr-icon">🔗</span>
                            </button>
                            <button
                              className="qr-payment-btn"
                              onClick={() => {
                                setShowGenerateTicketsModal({ booking, show: true });
                                setTicketCount(1);
                                setGenerateError('');
                              }}
                              title="توليد تذاكر إضافية"
                              style={{ backgroundColor: '#4caf50' }}
                            >
                              <span className="qr-icon">🎫</span>
                            </button>
                          </div>
                        )}
                      </div>
                      
                      <div className="card-info">
                        <div className="info-row">
                          <span className="label">التاريخ (هجري):</span>
                          <span className="value">{formatDate(booking.eventDate)}</span>
                        </div>
                        
                        <div className="info-row">
                          <span className="label">التاريخ (ميلادي):</span>
                          <span className="value">
                            {new Date(booking.eventDate).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                        
                        <div className="info-row">
                          <span className="label">وقت بدء المناسبة:</span>
                          <span className="value">{booking.eventTime}</span>
                        </div>
                        
                        <div className="info-row">
                          <span className="label">الموقع:</span>
                          <span className="value">{booking.location}</span>
                        </div>
                        
                        <div className="info-row">
                          <span className="label">نوع الحجز:</span>
                          <span className="value">{getBookingTypeLabel(booking.bookingType)}</span>
                        </div>
                        
                        <div className="info-row">
                          <span className="label">نوع البوث:</span>
                          <span className="value">
                            {booking.boothDisplayName || 'نوع البوث غير محدد'}
                          </span>
                        </div>
                        
                        {booking.bookingType === 'perPhoto' && (
                          <>
                            <div className="info-row">
                              <span className="label">التذاكر المجانية:</span>
                              <span className="value">{booking.freeTickets || booking.totalTickets || 0}</span>
                            </div>
                            <div className="info-row">
                              <span className="label">التذاكر الإضافية:</span>
                              <span className="value">
                                {(booking.additionalTickets || 0) + (booking.additionalTicketsCount || 0)}
                                {(booking.additionalTicketsCount || 0) > 0 && (
                                  <span style={{ color: '#4caf50', fontSize: '0.9em' }}>
                                    {' '}(+{booking.additionalTicketsCount} مشتراة بعد الحجز)
                                  </span>
                                )}
                              </span>
                            </div>
                            <div className="info-row">
                              <span className="label">حالة الاستخدام:</span>
                              <span className="value">
                                <span style={{ color: '#f44336' }}>{usedTickets}</span>
                                {' / '}
                                <span style={{ color: '#4caf50' }}>{(booking.freeTickets || 0) + (booking.additionalTickets || 0) + (booking.additionalTicketsCount || 0)}</span>
                                <span style={{ color: '#666', fontSize: '0.9em' }}>
                                  {' '}(متبقي: {remainingTickets})
                                </span>
                              </span>
                            </div>
                          </>
                        )}
                        
                        {booking.bookingType === 'hourly' && (
                          <div className="info-row">
                            <span className="label">عدد الساعات:</span>
                            <span className="value">{booking.hours} ساعات</span>
                          </div>
                        )}
                        
                        <div className="info-row">
                          <span className="label">السعر الإجمالي:</span>
                          <span className="value" style={{ fontWeight: 'bold' }}>
                            {booking.totalAmount?.toLocaleString('en-US') || 0} <RiyalSymbol size={14} />
                          </span>
                        </div>
                        
                        <div className="info-row">
                          <span className="label">العربون / المدفوع:</span>
                          <span className="value" style={{ color: '#27ae60', fontWeight: 'bold' }}>
                            {((booking.paidAmount ?? (booking.totalAmount || 0))).toLocaleString('en-US')} <RiyalSymbol size={14} />
                          </span>
                        </div>
                        
                        {(booking.totalAmount || 0) - ((booking.paidAmount ?? (booking.totalAmount || 0))) > 0 && (
                          <div className="info-row">
                            <span className="label">المبلغ المتبقي:</span>
                            <span className="value" style={{ color: '#e74c3c', fontWeight: 'bold' }}>
                              {((booking.totalAmount || 0) - ((booking.paidAmount ?? (booking.totalAmount || 0)))).toLocaleString('en-US')} <RiyalSymbol size={14} />
                              <button
                                onClick={() => {
                                  const url = `${window.location.origin}/pay-remaining/${booking.id || booking._id}`;
                                  navigator.clipboard.writeText(url);
                                  alert('تم نسخ رابط الدفع بنجاح');
                                }}
                                style={{
                                  marginRight: '10px',
                                  background: '#e74c3c',
                                  color: 'white',
                                  border: 'none',
                                  borderRadius: '5px',
                                  padding: '5px 10px',
                                  fontSize: '12px',
                                  cursor: 'pointer'
                                }}
                              >
                                🔗 نسخ رابط الدفع
                              </button>
                            </span>
                          </div>
                        )}
                        
                        <div className="info-row">
                          <span className="label">حالة الإيميل:</span>
                          <span className="value" style={{ 
                            color: booking.emailSent ? '#4caf50' : '#ff9800',
                            fontWeight: 'bold' 
                          }}>
                            {booking.emailSent ? '✅ تم الإرسال' : '⏳ لم يتم الإرسال'}
                          </span>
                        </div>
                        
                        <div className="info-row">
                          <span className="label">حالة التذاكر:</span>
                          <span className="value" style={{ 
                            color: booking.ticketsGenerated ? '#4caf50' : '#ff9800',
                            fontWeight: 'bold' 
                          }}>
                            {booking.ticketsGenerated ? '✅ تم إنشاؤها' : '⏳ لم تُنشأ بعد'}
                          </span>
                        </div>
                        
                        <div className="info-row">
                          <span className="label">إرسال التذاكر:</span>
                          <span className="value" style={{ 
                            color: booking.ticketsEmailSent ? '#4caf50' : '#ff9800',
                            fontWeight: 'bold' 
                          }}>
                            {booking.ticketsEmailSent ? '✅ تم الإرسال' : '⏳ لم يتم الإرسال'}
                          </span>
                        </div>
                      </div>
                      
                      <div className="card-actions">
                        <div style={{ display: 'flex', gap: '5px' }}>
                          <button onClick={() => { setEditingBooking(booking); setEditFormData({ paidAmount: booking.paidAmount ?? booking.totalAmount, totalAmount: booking.totalAmount }); }} className="action-btn" style={{ background: '#f39c12', color: 'white' }}>تعديل</button>
                          <button
                            onClick={() => generateInvoice(booking)}
                            className="action-btn invoice-btn"
                            style={{ backgroundColor: '#ff6b35', color: 'white' }}
                          >
                            🧾 استخراج فاتورة
                          </button>
                          <button
                            onClick={() => {
                              const bookingId = booking.id || booking._id;
                              window.open(`${process.env.REACT_APP_API_URL || 'https://fotomista-backend-production.up.railway.app'}/api/invoices/booking/${bookingId}/preview`, '_blank');
                            }}
                            className="action-btn"
                            style={{ backgroundColor: '#2196F3', color: 'white', fontSize: '12px' }}
                          >
                            👁️ معاينة الفاتورة
                          </button>
                        </div>
                        {booking.ticketsGenerated && booking.bookingType === 'perPhoto' && (
                          <a 
                            href={`/tickets/${booking.bookingCode}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="action-btn tickets-btn"
                            style={{ backgroundColor: '#4caf50' }}
                          >
                            🎫 عرض التذاكر
                          </a>
                        )}
                        
                        {booking.bookingType === 'perPhoto' && (
                          <button 
                            className="action-btn scan-btn"
                            onClick={() => handleShowScanDetails(booking)}
                          >
                            📊 تفاصيل المسح
                          </button>
                        )}
                        
                        <a 
                          href={`/pass/${booking.bookingCode}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="action-btn pass-btn"
                        >
                          🎟️ صفحة Pass
                        </a>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* Modal لإضافة حجز يدوي */}
        {showManualBookingModal && (
          <div className="manual-booking-modal" style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000,
            backdropFilter: 'blur(5px)'
          }}>
            <motion.div 
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              style={{
                background: 'white', borderRadius: '20px', padding: '30px',
                width: '90%', maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto',
                boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                border: '1px solid #eee'
              }}
            >
              <h2 style={{ textAlign: 'center', marginBottom: '20px', color: '#333', fontSize: '24px' }}>إضافة حجز يدوي</h2>
              
              <form onSubmit={handleCreateManualBooking} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <label style={{ fontSize: '14px', color: '#666', fontWeight: 'bold' }}>اسم العميل *</label>
                    <input required type="text" placeholder="مثال: أحمد محمد" value={newBookingData.customerName} onChange={e => setNewBookingData({...newBookingData, customerName: e.target.value})} style={{ padding: '12px 15px', borderRadius: '10px', border: '1px solid #ddd', fontSize: '15px', outline: 'none' }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <label style={{ fontSize: '14px', color: '#666', fontWeight: 'bold' }}>رقم الجوال *</label>
                    <input required type="text" placeholder="05XXXXXXXX" value={newBookingData.phone} onChange={e => setNewBookingData({...newBookingData, phone: e.target.value})} style={{ padding: '12px 15px', borderRadius: '10px', border: '1px solid #ddd', fontSize: '15px', outline: 'none' }} />
                  </div>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                  <label style={{ fontSize: '14px', color: '#666', fontWeight: 'bold' }}>البريد الإلكتروني *</label>
                  <input required type="email" placeholder="email@example.com" value={newBookingData.email} onChange={e => setNewBookingData({...newBookingData, email: e.target.value})} style={{ padding: '12px 15px', borderRadius: '10px', border: '1px solid #ddd', fontSize: '15px', outline: 'none' }} />
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <label style={{ fontSize: '14px', color: '#666', fontWeight: 'bold' }}>نوع الحجز</label>
                    <select value={newBookingData.bookingType} onChange={e => setNewBookingData({...newBookingData, bookingType: e.target.value})} style={{ padding: '12px 15px', borderRadius: '10px', border: '1px solid #ddd', fontSize: '15px', outline: 'none', backgroundColor: '#f9f9f9', cursor: 'pointer' }}>
                      <option value="hourly">نظام الساعات</option>
                      <option value="perPhoto">نظام التذاكر</option>
                    </select>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <label style={{ fontSize: '14px', color: '#666', fontWeight: 'bold' }}>نوع البوث</label>
                    <select value={newBookingData.boothType} onChange={e => {
                      const selectedBooth = configService.getBooths().find(b => b.slug === e.target.value);
                      setNewBookingData({...newBookingData, boothType: e.target.value, boothDisplayName: selectedBooth?.name || e.target.value});
                    }} style={{ padding: '12px 15px', borderRadius: '10px', border: '1px solid #ddd', fontSize: '15px', outline: 'none', backgroundColor: '#f9f9f9', cursor: 'pointer' }}>
                      {configService.getBooths().map(booth => (
                        <option key={booth.slug} value={booth.slug}>{booth.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <label style={{ fontSize: '14px', color: '#666', fontWeight: 'bold' }}>تاريخ المناسبة *</label>
                    <input required type="date" value={newBookingData.eventDate} onChange={e => setNewBookingData({...newBookingData, eventDate: e.target.value})} style={{ padding: '12px 15px', borderRadius: '10px', border: '1px solid #ddd', fontSize: '15px', outline: 'none' }} />
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <label style={{ fontSize: '14px', color: '#666', fontWeight: 'bold' }}>وقت المناسبة *</label>
                    <input required type="time" value={newBookingData.eventTime} onChange={e => setNewBookingData({...newBookingData, eventTime: e.target.value})} style={{ padding: '12px 15px', borderRadius: '10px', border: '1px solid #ddd', fontSize: '15px', outline: 'none' }} />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '15px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <label style={{ fontSize: '14px', color: '#666', fontWeight: 'bold' }}>المدينة *</label>
                    <select required value={newBookingData.city || ''} onChange={e => {
                        const selectedCity = cities.find(c => c._id === e.target.value);
                        setNewBookingData({
                          ...newBookingData, 
                          city: selectedCity?._id || '',
                          cityName: selectedCity?.name || '',
                          cityFee: selectedCity?.additionalPrice || 0
                        });
                      }} style={{ padding: '12px 15px', borderRadius: '10px', border: '1px solid #ddd', fontSize: '15px', outline: 'none', backgroundColor: '#f9f9f9', cursor: 'pointer' }}>
                      <option value="">اختر المدينة...</option>
                      {cities.map(city => (
                        <option key={city._id} value={city._id}>{city.name} {city.additionalPrice > 0 ? `(+${city.additionalPrice})` : ''}</option>
                      ))}
                    </select>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <label style={{ fontSize: '14px', color: '#666', fontWeight: 'bold' }}>الموقع / القاعة *</label>
                    <input required type="text" placeholder="اسم واسم الموقع" value={newBookingData.location} onChange={e => setNewBookingData({...newBookingData, location: e.target.value})} style={{ padding: '12px 15px', borderRadius: '10px', border: '1px solid #ddd', fontSize: '15px', outline: 'none' }} />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '15px', marginTop: '5px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <label style={{ fontSize: '14px', color: '#666', fontWeight: 'bold' }}>نسبة الخصم المئوية (%)</label>
                    <input type="number" min="0" max="100" placeholder="مثال: 10 أو صفر لعدم وجود خصم" value={newBookingData.discount || ''} onChange={e => setNewBookingData({...newBookingData, discount: Number(e.target.value) || 0})} style={{ padding: '12px 15px', borderRadius: '10px', border: '1px solid #ddd', fontSize: '15px', outline: 'none' }} />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', background: '#f5f7fa', padding: '15px', borderRadius: '12px', marginTop: '15px' }}>
                  {newBookingData.bookingType === 'hourly' ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      <label style={{ fontSize: '14px', color: '#333', fontWeight: 'bold' }}>عدد الساعات</label>
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <select 
                          value={customHoursMode ? 'custom' : newBookingData.hours} 
                          onChange={e => {
                            const val = e.target.value;
                            if (val === 'custom') {
                              setCustomHoursMode(true);
                              setNewBookingData({...newBookingData, hours: 1});
                            } else {
                              setCustomHoursMode(false);
                              setNewBookingData({...newBookingData, hours: Number(val)});
                            }
                            setIsCalculated(false); // trigger recalculate
                          }} 
                          style={{ padding: '12px', borderRadius: '10px', border: 'none', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', flex: 1, outline: 'none' }}
                        >
                          <option value="2">ساعتين</option>
                          <option value="3">3 ساعات</option>
                          <option value="4">4 ساعات</option>
                          <option value="custom">مخصص...</option>
                        </select>
                        {customHoursMode && (
                          <input type="number" min="1" placeholder="ساعات" value={newBookingData.hours} onChange={e => { setNewBookingData({...newBookingData, hours: Number(e.target.value)}); setIsCalculated(false); }} style={{ padding: '12px', borderRadius: '10px', border: '1px solid #ddd', width: '80px', outline: 'none' }} />
                        )}
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                      <label style={{ fontSize: '14px', color: '#333', fontWeight: 'bold' }}>إجمالي التذاكر للتوليد</label>
                      <input type="number" min="1" value={newBookingData.totalTickets} onChange={e => setNewBookingData({...newBookingData, totalTickets: Number(e.target.value), freeTickets: Number(e.target.value)})} style={{ padding: '12px 15px', borderRadius: '10px', border: 'none', boxShadow: '0 2px 5px rgba(0,0,0,0.05)', fontSize: '15px', outline: 'none' }} />
                    </div>
                  )}
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <label style={{ fontSize: '14px', color: '#333', fontWeight: 'bold' }}>المبلغ الإجمالي (ريال) *</label>
                    <input required type="number" value={newBookingData.totalAmount === 0 ? '' : newBookingData.totalAmount} onChange={e => { setNewBookingData({...newBookingData, totalAmount: Number(e.target.value)}); setIsCalculated(true); }} style={{ padding: '12px 15px', borderRadius: '10px', border: '1px solid #ddd', fontSize: '15px', outline: 'none' }} />
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <label style={{ fontSize: '14px', color: '#333', fontWeight: 'bold' }}>المبلغ المدفوع / العربون (ريال) *</label>
                    <input required type="number" value={newBookingData.paidAmount ?? newBookingData.totalAmount ?? ''} onChange={e => setNewBookingData({...newBookingData, paidAmount: Number(e.target.value)})} style={{ padding: '12px 15px', borderRadius: '10px', border: '2px solid #27ae60', fontSize: '15px', outline: 'none', fontWeight: 'bold', color: '#27ae60' }} />
                  </div>
                </div>
                
                <div style={{ display: 'flex', gap: '15px', marginTop: '15px' }}>
                  <button type="button" onClick={() => setShowManualBookingModal(false)} style={{ background: '#ecf0f1', color: '#7f8c8d', padding: '15px', borderRadius: '12px', border: 'none', flex: 1, cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', transition: 'all 0.2s' }}>
                    إلغاء
                  </button>
                  <button type="submit" disabled={creatingManualBooking} style={{ background: '#27ae60', color: 'white', padding: '15px', borderRadius: '12px', border: 'none', flex: 2, cursor: 'pointer', fontSize: '16px', fontWeight: 'bold', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '10px', boxShadow: '0 4px 15px rgba(39, 174, 96, 0.3)', transition: 'all 0.2s' }}>
                    {creatingManualBooking ? (
                      <>جاري الإضافة وإرسال الإيميلات...</>
                    ) : newBookingData.bookingType === 'perPhoto' ? 'حفظ وإرسال رابط التصميم' : 'حفظ الحجز'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

        {/* Modal تأكيد الحذف */}
        {showDeleteConfirm && (
          <div className="delete-confirm-modal" style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <motion.div 
              className="confirm-content"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.2 }}
              style={{
                background: 'white',
                borderRadius: '15px',
                padding: '40px',
                maxWidth: '500px',
                width: '90%',
                textAlign: 'center',
                boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
              }}
            >
              <div style={{ fontSize: '60px', marginBottom: '20px' }}>⚠️</div>
              <h2 style={{ color: '#e74c3c', marginBottom: '20px' }}>تأكيد الحذف</h2>
              <p style={{ fontSize: '18px', marginBottom: '20px' }}>
                هل أنت متأكد من حذف {selectedForDelete.length} حجز؟
              </p>
              <p style={{ color: '#666', marginBottom: '30px' }}>
                هذا الإجراء لا يمكن التراجع عنه وسيتم حذف جميع البيانات المتعلقة بهذه الحجوزات نهائياً.
              </p>
              
              <div style={{ marginBottom: '20px' }}>
                <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>الحجوزات المحددة للحذف:</p>
                <div style={{ 
                  maxHeight: '150px', 
                  overflowY: 'auto', 
                  background: '#f8f9fa', 
                  padding: '10px', 
                  borderRadius: '8px',
                  textAlign: 'right'
                }}>
                  {selectedForDelete.map(id => {
                    const booking = bookings.find(b => (b.id || b._id) === id);
                    return booking ? (
                      <div key={id} style={{ padding: '5px 0', borderBottom: '1px solid #e0e0e0' }}>
                        {booking.customerName} - #{booking.bookingCode}
                      </div>
                    ) : null;
                  })}
                </div>
              </div>
              
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
                <button
                  onClick={confirmDelete}
                  style={{
                    background: '#e74c3c',
                    color: 'white',
                    border: 'none',
                    padding: '12px 40px',
                    borderRadius: '25px',
                    fontSize: '16px',
                    cursor: 'pointer',
                    fontWeight: 'bold'
                  }}
                >
                  نعم، احذف
                </button>
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  style={{
                    background: '#95a5a6',
                    color: 'white',
                    border: 'none',
                    padding: '12px 40px',
                    borderRadius: '25px',
                    fontSize: '16px',
                    cursor: 'pointer'
                  }}
                >
                  إلغاء
                </button>
              </div>
            </motion.div>
          </div>
        )}

        {/* Modal لعرض تفاصيل المسح */}
        {showScanDetails?.show && (
          <div className="scan-details-modal" onClick={() => setShowScanDetails(null)}>
            <motion.div 
              className="modal-content"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.2 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3>تفاصيل مسح التذاكر</h3>
                <button 
                  className="close-btn"
                  onClick={() => setShowScanDetails(null)}
                >
                  ✕
                </button>
              </div>
              
              <div className="modal-body">
                <div className="booking-info">
                  <p><strong>رقم الحجز:</strong> {showScanDetails.booking.bookingCode}</p>
                  <p><strong>اسم العميل:</strong> {showScanDetails.booking.customerName}</p>
                  <p><strong>إجمالي التذاكر:</strong> {showScanDetails.booking.totalTickets}</p>
                </div>
                
                <div className="tickets-grid">
                  {loadingTickets ? (
                    <div className="loading-tickets">جاري تحميل التذاكر...</div>
                  ) : tickets.map((ticket, index) => (
                    <div 
                      key={ticket.id} 
                      className={`ticket-card ${ticket.used ? 'used' : 'unused'} ${ticket.type === 'additional' ? 'additional-ticket' : ''}`}
                    >
                      <div className="ticket-number">
                        تذكرة {ticket.ticketNumber}
                        {ticket.type === 'additional' && (
                          <span className="additional-badge">إضافية</span>
                        )}
                      </div>
                      <div className="ticket-status">
                        {ticket.used ? (
                          <>
                            <span className="status-icon">✓</span>
                            <span className="status-text">مستخدمة</span>
                            <div className="scan-time">
                              {new Date(ticket.usedAt!).toLocaleString('ar-SA', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit'
                              })}
                            </div>
                          </>
                        ) : (
                          <>
                            <span className="status-icon">✗</span>
                            <span className="status-text">غير مستخدمة</span>
                            {ticket.type === 'additional' && ticket.purchaseDate && (
                              <div className="purchase-date">
                                اشتريت في: {new Date(ticket.purchaseDate).toLocaleDateString('ar-SA')}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="summary-stats">
                  <div className="stat">
                    <span className="stat-label">التذاكر المستخدمة:</span>
                    <span className="stat-value used">{tickets.filter(t => t.used).length}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">التذاكر المتبقية:</span>
                    <span className="stat-value remaining">{tickets.filter(t => !t.used).length}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">التذاكر الأصلية:</span>
                    <span className="stat-value original">{tickets.filter(t => t.type === 'original').length}</span>
                  </div>
                  <div className="stat">
                    <span className="stat-label">التذاكر الإضافية:</span>
                    <span className="stat-value additional">{tickets.filter(t => t.type === 'additional').length}</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        )}

        {/* Modal لعرض QR code رابط الدفع */}
        {showPaymentQR?.show && (() => {
          const paymentUrl = `${window.location.origin}/additional-payment/${showPaymentQR.booking.id}`;
          
          return (
            <div className="payment-qr-modal" onClick={() => setShowPaymentQR(null)}>
              <motion.div 
                className="modal-content"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="modal-header">
                  <h3>رابط شراء تذاكر إضافية</h3>
                  <button 
                    className="close-btn"
                    onClick={() => setShowPaymentQR(null)}
                  >
                    ✕
                  </button>
                </div>
                
                <div className="modal-body">
                  <div className="qr-container">
                    <QRCode 
                      value={paymentUrl}
                      size={256}
                      style={{ height: "auto", maxWidth: "100%", width: "100%" }}
                      viewBox={`0 0 256 256`}
                    />
                  </div>
                  
                  <div className="payment-info">
                    <p className="info-text">امسح هذا الرمز للوصول لصفحة شراء تذاكر إضافية</p>
                    <div className="url-section">
                      <input 
                        type="text" 
                        value={paymentUrl} 
                        readOnly 
                        className="url-input"
                      />
                      <button 
                        className="copy-btn"
                        onClick={() => {
                          navigator.clipboard.writeText(paymentUrl);
                          alert('تم نسخ الرابط');
                        }}
                      >
                        نسخ
                      </button>
                    </div>
                    <a 
                      href={paymentUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="direct-link"
                    >
                      فتح الرابط
                    </a>
                  </div>
                </div>
              </motion.div>
            </div>
          );
        })()}

        {/* Generate Tickets Modal */}
        {showGenerateTicketsModal?.show && (() => {
          const booking = showGenerateTicketsModal.booking;
          return (
            <div className="modal-overlay" onClick={() => setShowGenerateTicketsModal(null)}>
              <motion.div
                className="modal-content"
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.2 }}
                onClick={(e) => e.stopPropagation()}
                style={{
                  maxWidth: '500px',
                  padding: '30px',
                  background: 'white',
                  borderRadius: '15px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.3)'
                }}
              >
                <div className="modal-header">
                  <h2>توليد تذاكر إضافية</h2>
                  <button
                    className="close-btn"
                    onClick={() => setShowGenerateTicketsModal(null)}
                  >
                    ✕
                  </button>
                </div>

                <div className="modal-body" style={{ padding: '20px 0' }}>
                  <p style={{ marginBottom: '15px' }}>
                    <strong>الحجز:</strong> {booking.customerName} - #{booking.bookingCode}
                  </p>

                  <div style={{ marginBottom: '20px' }}>
                    <label style={{ display: 'block', marginBottom: '10px' }}>
                      عدد التذاكر المطلوب توليدها:
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="50"
                      value={ticketCount}
                      onChange={(e) => setTicketCount(Math.min(50, Math.max(1, parseInt(e.target.value) || 1)))}
                      style={{
                        width: '100%',
                        padding: '10px',
                        fontSize: '16px',
                        border: '1px solid #ddd',
                        borderRadius: '5px'
                      }}
                    />
                  </div>

                  {generateError && (
                    <div style={{
                      padding: '10px',
                      background: '#fee',
                      color: '#c00',
                      borderRadius: '5px',
                      marginBottom: '15px'
                    }}>
                      {generateError}
                    </div>
                  )}

                  <div style={{
                    padding: '15px',
                    background: '#fff9e6',
                    borderRadius: '5px',
                    marginBottom: '20px'
                  }}>
                    <p style={{ margin: 0, color: '#666' }}>
                      <strong>ملاحظة:</strong> سيتم توليد {ticketCount} تذاكر إضافية وإرسالها إلى البريد الإلكتروني المسجل للحجز.
                    </p>
                  </div>
                </div>

                <div className="modal-footer" style={{ display: 'flex', gap: '10px' }}>
                  <button
                    onClick={handleGenerateTickets}
                    disabled={generatingTickets}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: generatingTickets ? '#ccc' : '#4caf50',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: generatingTickets ? 'not-allowed' : 'pointer',
                      fontWeight: 'bold'
                    }}
                  >
                    {generatingTickets ? 'جاري التوليد...' : `توليد ${ticketCount} تذاكر`}
                  </button>
                  <button
                    onClick={() => setShowGenerateTicketsModal(null)}
                    disabled={generatingTickets}
                    style={{
                      flex: 1,
                      padding: '12px',
                      background: '#666',
                      color: 'white',
                      border: 'none',
                      borderRadius: '5px',
                      cursor: generatingTickets ? 'not-allowed' : 'pointer'
                    }}
                  >
                    إلغاء
                  </button>
                </div>
              </motion.div>
            </div>
          );
        })()}

        {/* Modal تعديل الحجز */}
        {editingBooking && (
          <div className="generate-tickets-modal" style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 1000
          }}>
            <motion.div 
              className="modal-content"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.2 }}
              style={{
                background: 'white', borderRadius: '20px', padding: '30px',
                width: '90%', maxWidth: '500px', boxShadow: '0 10px 30px rgba(0,0,0,0.3)',
                maxHeight: '90vh', overflowY: 'auto'
              }}
            >
              <h2 style={{ color: '#2c3e50', marginBottom: '20px', textAlign: 'right' }}>تعديل بيانات الحجز: #{editingBooking.bookingCode}</h2>
              <form onSubmit={handleSaveEdit} style={{ textAlign: 'right' }}>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px' }}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <label style={{ fontSize: '14px', color: '#333', fontWeight: 'bold' }}>السعر الإجمالي (ريال)</label>
                    <input 
                      type="number" 
                      required 
                      value={editFormData.totalAmount || ''} 
                      onChange={e => setEditFormData({...editFormData, totalAmount: Number(e.target.value)})} 
                      style={{ padding: '12px 15px', borderRadius: '10px', border: '1px solid #ddd', fontSize: '15px', outline: 'none' }} 
                    />
                  </div>
                  
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                    <label style={{ fontSize: '14px', color: '#333', fontWeight: 'bold' }}>المبلغ المدفوع (ريال)</label>
                    <input 
                      type="number" 
                      required 
                      value={editFormData.paidAmount ?? ''} 
                      onChange={e => setEditFormData({...editFormData, paidAmount: Number(e.target.value)})} 
                      style={{ padding: '12px 15px', borderRadius: '10px', border: '2px solid #3498db', fontSize: '15px', outline: 'none', fontWeight: 'bold' }} 
                    />
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '15px', marginTop: '20px' }}>
                  <button type="button" onClick={() => setEditingBooking(null)} style={{ background: '#ecf0f1', color: '#7f8c8d', padding: '12px', borderRadius: '10px', border: 'none', flex: 1, cursor: 'pointer', fontSize: '15px', fontWeight: 'bold' }}>
                    إلغاء
                  </button>
                  <button type="submit" disabled={isSavingEdit} style={{ background: '#3498db', color: 'white', padding: '12px', borderRadius: '10px', border: 'none', flex: 2, cursor: 'pointer', fontSize: '15px', fontWeight: 'bold' }}>
                    {isSavingEdit ? 'جاري الحفظ...' : 'حفظ التعديلات'}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        )}

      </motion.div>
    </AdminLayout>
  );
}

export default BookingsManagement;